import { useCallback, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Modal, ServiceError, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

import { useTracker } from "../../hooks/useTracker";
import { TOTPErrorCode, useEnrollTOTP, useVerifyTOTP } from "../../hooks/useTwoFactor";
import { ModalClosedAction, ModalShownAction } from "../../lib/TrackingAdapter";
import { GenericErrorModal } from "../Modal/Modal";

import style from "./SetupTwoFactorModal.module.css";

const verificationCodeSchema = z.object({
  code: z.string().length(6, "Code must be 6 digits"),
});

export type SetupTwoFactorModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (recoveryCode: string) => void;
};

type VerificationFormData = z.infer<typeof verificationCodeSchema>;

export default function SetupTwoFactorModal({
  open,
  onClose,
  onSuccess,
}: SetupTwoFactorModalProps) {
  const tracker = useTracker();
  const {
    data: totpEnrollData,
    isPending: isEnrollTOTPPending,
    isError: isEnrollTOTPError,
    refetch: refetchEnrollTOTP,
  } = useEnrollTOTP({ enabled: open });

  const { mutateAsync: mutateVerifyTOTP } = useVerifyTOTP({
    onError: (error) => {
      let errorMessage = "Something went wrong. Please try setting up again.";

      if (error instanceof ServiceError) {
        const errorCode = error.serviceErrorResponse.code;
        if (errorCode === TOTPErrorCode.InvalidCode) {
          errorMessage = "Invalid code. Please try again.";
        } else if (errorCode === TOTPErrorCode.AlreadyEnabled) {
          errorMessage = "Two-factor authentication is already enabled.";
        } else if (errorCode === TOTPErrorCode.DeviceNotFound) {
          errorMessage = "Something went wrong. Please try setting up again.";
        } else if (errorCode === TOTPErrorCode.DeviceIdMismatch) {
          errorMessage = "Something went wrong. Please try setting up again.";
        }
      }

      setError("code", { message: errorMessage });
    },
  });

  const {
    setError,
    clearErrors,
    formState: { errors, isSubmitting, isSubmitted, isValid },
    control,
    handleSubmit,
    reset,
  } = useForm<VerificationFormData>({
    resolver: zodResolver(verificationCodeSchema),
    reValidateMode: "onSubmit",
    defaultValues: {
      code: "",
    },
  });

  useEffect(() => {
    if (open) {
      refetchEnrollTOTP();
      reset();
    }
  }, [open, refetchEnrollTOTP, reset]);

  const onSubmit = useCallback(
    async (data: VerificationFormData) => {
      if (!totpEnrollData?.totpDeviceID) return;

      try {
        await mutateVerifyTOTP({ totpDeviceID: totpEnrollData.totpDeviceID, code: data.code });
        if (totpEnrollData.recoveryCode) {
          onSuccess(totpEnrollData.recoveryCode);
        }
      } catch (error) {
        // Error is handled by useEffect
      }
    },
    [totpEnrollData, mutateVerifyTOTP, onSuccess]
  );

  const handleVerificationCodeComplete = useCallback(async () => {
    await handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleCopySecret = useCallback(async () => {
    if (totpEnrollData?.key) {
      try {
        await navigator.clipboard.writeText(totpEnrollData.key);
        toast.success("Secret key copied to clipboard!");
      } catch (error) {
        toast.error("Failed to copy secret key to clipboard");
      }
    }
  }, [totpEnrollData]);

  const isLoading = isEnrollTOTPPending;

  if (isEnrollTOTPError) {
    return <GenericErrorModal open={open} onClose={onClose} />;
  }

  return (
    <Modal
      isLoading={isLoading}
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.TWO_FACTOR_ENABLE_INTENT)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.TWO_FACTOR_ENABLE_INTENT);
        onClose();
      }}
      size="medium"
      title="Set up 2FA"
      footer={
        <div className={style.footer}>
          <Button variant="grayscale" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || (!isValid && isSubmitted)}
          >
            Enable 2FA
          </Button>
        </div>
      }
    >
      <div className={style.content}>
        <p className={style.description}>
          Add an extra layer of protection to your account. When you sign in, you&apos;ll confirm
          it&apos;s really you by entering a 6-digit code from an authenticator app.
        </p>

        <div className={style.section}>
          <span className={style.sectionTitle}>
            Step 1: Scan this code with an authenticator app
          </span>
          <p className={style.sectionDescription}>
            Download an authenticator app like Authy, 1Password, Microsoft Authenticator or Google
            Authenticator and scan this QR code.
          </p>

          <div className={style.qrCodeContainer}>
            <img
              src={`data:image/png;base64,${totpEnrollData?.verificationImage}`}
              alt="QR Code"
              className={style.qrCode}
            />
            <div className={style.secretKeyContainer}>
              <span className={style.secretKey}>{totpEnrollData?.key}</span>
              <Button
                className={style.copySecretButton}
                variant="grayscale"
                onClick={handleCopySecret}
              >
                Copy to clipboard
              </Button>
            </div>
          </div>
        </div>

        <div className={style.section}>
          <span className={style.sectionTitle}>Step 2: Enter your Authentication code</span>
          <p className={style.sectionDescription}>
            Enter the 6-digit verification code from your authenticator app.
          </p>

          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <TOTPCodeInput
                className={style.codeInput}
                onChange={(value) => {
                  field.onChange(value);
                  clearErrors("code");
                }}
                onComplete={handleVerificationCodeComplete}
                error={Boolean(errors.code)}
                errorMessage={errors.code?.message}
                disabled={isLoading || isSubmitting}
                autoFocus
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
}
